/**
 * User store — holds the active organization/team, bootstrapped from GET /api/me.
 * The active team must be one the user actually belongs to, or the SSE update
 * stream rejects the subscription.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface Organization {
  id: string;
  name: string;
  slug: string;
}

interface Team {
  id: string;
  name: string;
  organizationId: string;
}

interface UserStore {
  activeOrganization: Organization | null;
  activeTeam: Team | null;
  organizations: Organization[];
  teams: Team[];
  /** DB-backed feature flags resolved server-side, bootstrapped from GET /api/me. */
  features: string[];
  /** From GET /api/me: false while the current Terms wait for the user's agreement, null until /me answers. */
  termsAccepted: boolean | null;

  setActiveOrganization: (org: Organization | null) => void;
  setActiveTeam: (team: Team | null) => void;
  setOrganizations: (orgs: Organization[]) => void;
  setTeams: (teams: Team[]) => void;
  setFeatures: (features: string[]) => void;
  setTermsAccepted: (accepted: boolean) => void;

  clearUserData: () => void;
}

export const useUserStore = create<UserStore>()(
  persist(
    (set) => ({
      // Populated from GET /api/me after auth; null until then.
      activeOrganization: null,
      activeTeam: null,
      organizations: [],
      teams: [],
      features: [],
      termsAccepted: null,

      setActiveOrganization: (org) => set({ activeOrganization: org }),
      setActiveTeam: (team) => set({ activeTeam: team }),
      setOrganizations: (orgs) => set({ organizations: orgs }),
      setTeams: (teams) => set({ teams }),
      setFeatures: (features) => set({ features }),
      setTermsAccepted: (termsAccepted) => set({ termsAccepted }),

      clearUserData: () => set({
        activeOrganization: null,
        activeTeam: null,
        organizations: [],
        teams: [],
        features: [],
        termsAccepted: null,
      }),
    }),
    {
      name: 'user-store',
      partialize: ({ activeOrganization, activeTeam, organizations, teams, features }) => ({ activeOrganization, activeTeam, organizations, teams, features }),
    }
  )
);